// src/views/StudentDashboard.js
import React, { useState, useEffect } from 'react';
import { Card, CardBody, CardTitle, Button, Row, Col, Table, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { Link } from 'react-router-dom';
import axios from '../../api/Axios';
import StudentFormWizard from '../../forms/StudentFormWizard';

const StudentDashboard = () => {
  const reg_status = localStorage.getItem('reg_status');
  const [studentData, setStudentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLinkRecordsModal, setShowLinkRecordsModal] = useState(false);
  const [showSkipWarning, setShowSkipWarning] = useState(false);
  const [idNumber, setIdNumber] = useState('');
  const [checkingRecords, setCheckingRecords] = useState(false);
  const [beneficiaryData, setBeneficiaryData] = useState(null);
  const [recordsFound, setRecordsFound] = useState(null); // null = not checked, true = found, false = not found
  
  useEffect(() => {
    const fetchStudentData = async () => {
      if (reg_status === '1') {
        try {
          const userId = localStorage.getItem('user_id');
          if (userId) {
            const response = await axios.get('/accounts/user/');
            console.log('Student data received:', response.data);
            setStudentData(response.data);
          }
        } catch (error) {
          console.error('Error fetching student data:', error);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    fetchStudentData();
  }, [reg_status]);

  // Show modal only for reg_status === '0' on component mount
  useEffect(() => {
    if (reg_status === '0') {
      setShowLinkRecordsModal(true);
    }
  }, [reg_status]);

  const handleCheckRecords = async () => {
    if (!idNumber) return;
    
    setCheckingRecords(true);
    try {
      const response = await axios.get(`/accounts/check-beneficiary-by-id/?national_id=${idNumber}`);
      console.log('Beneficiary check response:', response.data);
      
      if (response.data.exists) {
        setRecordsFound(true);
        setBeneficiaryData(response.data.data);
      } else {
        setRecordsFound(false);
      }
    } catch (error) {
      console.error('Error checking beneficiary:', error);
      setRecordsFound(false);
    } finally {
      setCheckingRecords(false);
    }
  };

  const handleConfirmSync = () => {
    // Close modal - no auto-fill for now
    setShowLinkRecordsModal(false);
  };

  const handleSkipLinking = () => {
    setShowSkipWarning(true);
  };

  const handleProceedWithoutLinking = () => {
    setShowLinkRecordsModal(false);
    setShowSkipWarning(false);
  };

  const formatValue = (value) => {
    if (value === null || value === undefined || value === '') {
      return 'Not provided';
    }
    if (value === 'pending') {
      return 'Pending completion';
    }
    return value;
  };
  
  return (
    <>
      {/* Link Records Modal - Shows only for reg_status === '0' */}
      <Modal isOpen={showLinkRecordsModal} toggle={() => {}} backdrop="static" keyboard={false}>
        <ModalHeader>Link your records</ModalHeader>
        <ModalBody>
          {!showSkipWarning && recordsFound === null && (
            <>
              <p>Enter your ID number to check for existing bursary data and verify your identity. If no match is found, you can proceed to complete your profile manually.</p>
              <div className="form-floating mb-3">
                <input
                  className="form-control"
                  type="text"
                  id="idNumberInput"
                  value={idNumber}
                  onChange={(e) => setIdNumber(e.target.value)}
                  placeholder="ID Number"
                  disabled={checkingRecords}
                  maxLength="13"
                />
                <label htmlFor="idNumberInput">ID Number</label>
              </div>
            </>
          )}
          
          {recordsFound === false && !showSkipWarning && (
            <div className="alert alert-warning mb-0">
              <i className="fas fa-exclamation-triangle me-2"></i>
              No records found. Please contact your BMO.
            </div>
          )}
          
          {recordsFound === true && !showSkipWarning && (
            <div className="alert alert-success mb-3">
              <i className="fas fa-check-circle me-2"></i>
              <strong>Records found!</strong>
              <div className="mt-2">
                <p className="mb-1"><strong>Name:</strong> {beneficiaryData?.first_name_other} {beneficiaryData?.surname}</p>
                <p className="mb-1"><strong>Email:</strong> {beneficiaryData?.email_address}</p>
                <p className="mb-1"><strong>Bursary:</strong> {beneficiaryData?.bursary_name}</p>
              </div>
              <p className="mt-3 mb-0">Click <strong>Confirm and Sync</strong> to automatically fill your registration form with this data.</p>
            </div>
          )}
          
          {showSkipWarning && (
            <div className="alert alert-warning mb-0">
              <i className="fas fa-exclamation-triangle me-2"></i>
              You will be registered as a private student and your profile will not be linked to any Bursary Management Office.
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          {!showSkipWarning && recordsFound === null && (
            <>
              <Button color="secondary" onClick={handleSkipLinking} disabled={checkingRecords}>
                Skip
              </Button>
              <Button color="primary" onClick={handleCheckRecords} disabled={!idNumber || checkingRecords}>
                {checkingRecords ? 'Checking...' : 'Check for Records'}
              </Button>
            </>
          )}
          
          {recordsFound === false && !showSkipWarning && (
            <>
              <Button color="secondary" onClick={handleSkipLinking}>
                Skip
              </Button>
              <Button color="primary" onClick={() => { setRecordsFound(null); setIdNumber(''); }}>
                Try Again
              </Button>
            </>
          )}
          
          {recordsFound === true && !showSkipWarning && (
            <>
              <Button color="secondary" onClick={handleSkipLinking}>
                Skip
              </Button>
              <Button color="success" onClick={handleConfirmSync}>
                <i className="fas fa-check me-2"></i>
                Confirm and Sync
              </Button>
            </>
          )}
          
          {showSkipWarning && (
            <Button color="primary" onClick={handleProceedWithoutLinking}>
              Proceed
            </Button>
          )}
        </ModalFooter>
      </Modal>

      {reg_status === '0' && (
        <div>
          <h3 className="mb-4">Complete Your Registration</h3>
          <StudentFormWizard />
        </div>
      )}

      {reg_status === '1' && (
        <div>
          <Row>
            {/* Welcome Card */}
            <Col md="6" className="mb-4">
              <Card>
                <CardBody>
                  <CardTitle tag="h5">Welcome Back!</CardTitle>
                  <p>Your registration is complete.</p>
                </CardBody>
              </Card>
            </Col>

            {/* Student Details Card */}
            <Col md="6" className="mb-4">
              <Card>
                <CardBody>
                  <CardTitle tag="h5">Profile</CardTitle>
                  {loading ? (
                    <p>Loading your details...</p>
                  ) : studentData ? (
                    <Table size="sm" borderless>
                      <tbody>
                        <tr>
                          <td><strong>Name:</strong></td>
                          <td>{formatValue(studentData.title)} {formatValue(studentData.other_names)} {formatValue(studentData.surname)}</td>
                        </tr>
                        <tr>
                          <td><strong>Email:</strong></td>
                          <td>{formatValue(studentData.user?.email)}</td>
                        </tr>
                        <tr>
                          <td><strong>ID Number:</strong></td>
                          <td>{formatValue(studentData.id_number)}</td>
                        </tr>
                        <tr>
                          <td><strong>Phone:</strong></td>
                          <td>{formatValue(studentData.phone_number)}</td>
                        </tr>
                        <tr>
                          <td><strong>Gender:</strong></td>
                          <td>{formatValue(studentData.gender)}</td>
                        </tr>
                      </tbody>
                    </Table>
                  ) : (
                    <p>Unable to load your details.</p>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      )}

      {reg_status === '2' && (
        <Card>
          <CardBody>
            <CardTitle tag="h5">Student Dashboard</CardTitle>
            <p>Advanced student status</p>
            {/* Add student-specific content here */}
          </CardBody>
        </Card>
      )}
    </>
  );
};

export default StudentDashboard;